import { createSelector } from '@reduxjs/toolkit';
import { CONSTANTS_SETTINGS } from 'config/settingsContainer';
import { USER_TYPE } from 'config/userType';
import { rolesConfig } from 'config/rolesConfig';
import { filterPeopleByFilters } from 'library/helpers/bookingHelpers';
import { selectRoles } from 'reduxStore/roles/selector';
import { selectSites } from 'reduxStore/sites/selectors';
import { selectCurrentProfileOrgSettings } from 'reduxStore/settings/selectors';


//📌 Selector for get all people from current profile
export const selectPeople = createSelector(
	[(state) => state.People.data, (state) => state.Auth.selectedProfile.orgID],
	(peopleData, orgID) => peopleData[orgID] || []
);

//📌 Selector for login-capable people based on Inner Range setting
export const selectLoginCapablePeople = createSelector(
	[selectPeople, selectCurrentProfileOrgSettings],
	(people, orgSettings) => {
		const innerRangeEnabled = orgSettings.some(
			(setting) =>
				setting.defaultSettingID === CONSTANTS_SETTINGS.INNER_RANGE_MODULE && Number(setting.value) === 1
		);

		if (innerRangeEnabled) {
			return people;
		}

		return people.filter((person) => person.userTypeID !== USER_TYPE.ACCESS_ONLY_USER);
	}
);

// 🧠 SELECTOR: Person + Admin Roles
export const selectPeopleWithAdminRoles = createSelector(
	[selectPeople, selectRoles, selectSites],
	(people, roles, sites) => {
		return people.map((person) => {
			// 🔥 Get roles for this person
			const personRoles = roles.filter((role) => role.personID === person.personID);

			// 🔥 Filtrar por tipo de permiso
			const organisationRoles = personRoles
				.filter((role) => role.permissionID === rolesConfig.GLOBAL_ADMIN && role.entityTypeID === 1)
				.map((role) => role.entityID);

			const siteRoles = personRoles
				.filter((role) => role.permissionID === rolesConfig.SITE_ADMIN && role.entityTypeID === 2)
				.map((role) => {
					// 🔥 Buscar el nombre del site
					const siteInfo = sites.find((site) => site.siteID === role.entityID);
					return {
						siteID: role.entityID,
						displayName: siteInfo?.name || '-'
					};
				});

			return {
				...person,
				admin: {
					organisation: organisationRoles,
					sites: siteRoles
				}
			};
		});
	}
);

//📌 Selector for get all people from current profile and loading.
export const selectPeopleAndLoading = createSelector(
	[(state) => state.People.data, (state) => state.Auth.selectedProfile.orgID, (state) => state.People.loading],
	(peopleData, orgID, loading) => ({
		people: peopleData[orgID] || [],
		loading
	})
);

//📌 Selector for get person by personID.
export const selectPeopleByPersonID = createSelector(
	[(state) => state.People.data, (state) => state.Auth.selectedProfile.orgID, (_, personID) => personID],
	(peopleData, orgID, personID) => (peopleData[orgID] || []).find((person) => person.personID === personID) || null
);

//📌 Selector for get dependant people by personID.
export const selectDependantPeopleByPersonID = createSelector(
	[(state) => state.People.data, (state) => state.Auth.selectedProfile.orgID, (_, personID) => personID],
	(peopleData, orgID, personID) =>
		(peopleData[orgID] || []).filter((person) => person.masterUserID === personID) || []
);

//📌 Selector for get all people by siteID.
export const selectPeopleBySiteID = createSelector(
	[(state) => state.People.data, (state) => state.Auth.selectedProfile.orgID, (_, siteID) => siteID],
	(peopleData, orgID, siteID) => (peopleData[orgID] || []).filter((person) => person.siteID === siteID) || null
);

// 📌 Selector: Active Users
export const selectActiveUsers = createSelector([selectPeople], (people) => {
	return people.filter((person) => person.isActive && !person.isNewUser).length;
});

// 📌 Selector: Inactive Users
export const selectInactiveUsers = createSelector([selectPeople], (people) => {
	return (
		people.filter((person) => person.isActive && person.isNewUser).length +
		people.filter((person) => !person.isActive && !person.isNewUser).length
	);
});

// 📌 **Selector: Get Data for Pie Chart**
export const selectActiveAndInactiveCounts = createSelector(
	[selectActiveUsers, selectInactiveUsers],
	(activeCount, inactiveCount) => [
		{ name: 'Active', value: activeCount },
		{ name: 'Inactive', value: inactiveCount }
	]
);

//📌 Selector people by name
export const selectPeopleByName = createSelector(
	[(state) => state.People.data, (state) => state.Auth.selectedProfile.orgID, (_, name) => name],
	(peopleData, orgID, name) =>
		(peopleData[orgID] || []).filter(
			(person) =>
				person.name?.toLowerCase().includes(name.toLowerCase()) ||
				person.email?.toLowerCase().includes(name.toLowerCase()) ||
				person.displayName?.toLowerCase().includes(name.toLowerCase())
		) || []
);

//📌 Selector people by filters
export const selectPeopleByFilters = createSelector(
	[
		(state) => state.People.data,
		(state) => state.Bookings.data,
		(state) => state.LockersLogs.data,
		(state) => state.Auth.selectedProfile.orgID,
		(state) => state.People.loading,
		(state, searchTerm) => searchTerm,
		(state, searchTerm, selectedFilters) => selectedFilters
	],
	(peopleData, bookingsData, logsData, orgID, loading, searchTerm, selectedFilters) => {
		const orgPeople = peopleData[orgID] || [];
		const orgBookings = bookingsData[orgID] || [];
		const orgLogs = logsData[orgID] || [];

		// 🔍 Apply search filter
		let filteredPeople = orgPeople;
		if (searchTerm) {
			filteredPeople = filteredPeople.filter((person) =>
				`${person.firstName} ${person.lastName}`.toLowerCase().includes(searchTerm.toLowerCase())
			);
		}

		// 🎯 Apply active filters
		const finalPeople = filterPeopleByFilters(filteredPeople, orgBookings, orgLogs, selectedFilters);

		return {
			people: finalPeople,
			bookingsData: orgBookings,
			logsData: orgLogs,
			loading
		};
	}
);

//📌 Selector for selected users for invitation
export const selectSelectedUsers = createSelector(
	[(state) => state.People.selectedUsers],
	(selectedUsers) => selectedUsers
);

//📌 Selector for get contact by personID
export const selectContactByPersonID = createSelector(
	[(state) => state.People.data, (state) => state.Auth.selectedProfile.orgID, (_, personID) => personID],
	(peopleData, orgID, personID) =>
		(peopleData[orgID] || []).find((person) => person.personID === personID)?.contacts?.data?.[0] || null
);

//📌 Selector: Map<personID, displayName> for O(1) name lookups
export const selectPeopleNameMap = createSelector([selectPeople], (people) => {
	const map = new Map();
	people.forEach((p) => map.set(p.personID, p.displayName || 'Unknown'));
	return map;
});
